import { useCallback } from "react";
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  FormControl,
  Box,
  Typography,
} from "@mui/material";
import { AdminPostRouteReqDto } from "service/api/estabrookSchemas";
import { generateHourOptions } from "../../../utils/date-time";
import { DialogProps } from "@mui/material";
import { ControlledSelect, ControlledSelectOption, makeSxStyles } from "ui";
import { regColorOptions } from "../../../components/color-selector/color-selector";
import useRoutePlannerStore from "../../../state/route-planner-store";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { RouteStatus } from "./helpers/route-status.enum";
import useVirtualRouteForm from "./use-virtual-route-form";

export interface CreateVirtualRouteDialogProps
  extends Omit<
    DialogProps,
    "onClose" | "open" | "style" | "inputValue" | "handler"
  > {
  onClose: () => void;
  onRefresh: () => void;
  open?: boolean;
  inputValue?: string;
  handler?: (value: string) => void;
  cancelText?: string;
  confirmText?: string;
  currentRouteStatus?: RouteStatus;
}

type FieldOptionType = "TextField" | "Select";

interface FieldOptions {
  key: keyof AdminPostRouteReqDto;
  type: FieldOptionType;
  label: string;
  placeholder: string;
  options: ControlledSelectOption[];
  onChange: (value: string) => void;
}

const CreateVirtualRouteDialog: React.FC<CreateVirtualRouteDialogProps> = (
  props,
) => {
  const { date } = useParams();
  const selectedDate = date
    ? DateTime.fromFormat(date, "MM-dd-yyyy")
    : DateTime.now();
  const styles = useStyles();
  const {
    title = "Create Virtual Route",
    sx,
    onClose,
    onRefresh,
    open,
    cancelText = "Cancel",
    confirmText = "Create Route",
    currentRouteStatus,
    ...rest
  } = props;

  const { newVirtualRoutePriority, routes } = useRoutePlannerStore();
  const route = routes[newVirtualRoutePriority];

  const hourOptions = useCallback(() => {
    // Determine when to restrict past times based on route phase
    // In planning phase: allow all times (no restrictions)
    // In planned/live phases: restrict past times (user can only select future times)
    const shouldRestrictPastTimes = currentRouteStatus !== RouteStatus.Planning;

    return generateHourOptions(shouldRestrictPastTimes).filter((option) => {
      // Also ensure the selected hour is after the route start time
      return option.value > route.startTime;
    });
  }, [route.startTime, currentRouteStatus]);

  const colorOptions: ControlledSelectOption[] = regColorOptions.map(
    (color) => ({
      value: color.value,
      label: (
        <Box sx={styles.colorContainer}>
          <Box sx={{ ...styles.colorBox, backgroundColor: color.value }} />
          <Typography>{color.label}</Typography>
        </Box>
      ),
    }),
  );

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    errors,
    touched,
    resetForm,
    driverOptions,
  } = useVirtualRouteForm({
    date: selectedDate.toFormat("yyyy-MM-dd"),
    routePriority: newVirtualRoutePriority + 1,
    startTime: "",
    onSuccess: () => {
      onRefresh();
      onClose();
    },
  });

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  const fields: FieldOptions[] = [
    {
      key: "routeName",
      type: "TextField",
      label: "Create a virtual route named",
      placeholder: "Enter name",
      options: [],
      onChange: (value) => {
        setFieldValue("routeName", value);
        setFieldTouched("routeName", true);
      },
    },
    {
      key: "startTime",
      type: "Select",
      label: "Starting at",
      placeholder: "Select start time",
      options: hourOptions(),
      onChange: (value) => {
        setFieldValue("startTime", value);
        setFieldTouched("startTime", true);
      },
    },
    {
      key: "color",
      type: "Select",
      label: "With route color",
      placeholder: "Select color",
      options: colorOptions,
      onChange: (value) => {
        setFieldValue("color", value);
        setFieldTouched("color", true);
      },
    },
    {
      key: "driverId",
      type: "Select",
      label: "Route driver",
      placeholder: "Select driver",
      options: driverOptions,
      onChange: (value) => {
        setFieldValue("driverId", value);
        setFieldTouched("driverId", true);
      },
    },
  ];

  return (
    <Dialog onClose={onClose} {...rest} sx={sx || {}} open={open || false}>
      <DialogTitle variant="titleLarge" sx={styles.dialogTitle}>
        {title}
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          {fields.map((field) => (
            <Box key={field.key} sx={styles.fieldContainer}>
              <InputLabel sx={styles.inputLabel}>{field.label}</InputLabel>
              <FormControl sx={styles.defaultFlex}>
                {field.type === "TextField" && (
                  <TextField
                    sx={styles.defaultFlex}
                    placeholder={field.placeholder}
                    name={field.key}
                    value={values[field.key]}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={touched[field.key] && Boolean(errors[field.key])}
                    InputProps={{
                      style: { height: 36, borderColor: "white" },
                    }}
                  />
                )}
                {field.type === "Select" && (
                  <ControlledSelect
                    id={field.key}
                    name={field.key}
                    placeholderSelectEnabled={false}
                    value={values[field.key] as string}
                    variant="outlined"
                    placeholder={field.placeholder}
                    options={field.options}
                    onChange={(value) => field.onChange(value)}
                    error={touched[field.key] && Boolean(errors[field.key])}
                  />
                )}
              </FormControl>
            </Box>
          ))}
          <DialogActions sx={styles.dialogActions}>
            <Button
              onClick={onClose}
              sx={styles.cancelButton}
              data-testid="cancel-button"
            >
              {cancelText}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={styles.confirmButton}
              data-testid="confirm-button"
            >
              {confirmText}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeSxStyles(({ palette: { grey, primary } }) => ({
  dialogTitle: {
    color: grey[100],
    lineHeight: "28px",
    marginBottom: "24px",
  },
  dialogActions: {
    marginTop: "40px",
  },
  cancelButton: {
    color: primary.main,
  },
  confirmButton: {
    backgroundColor: primary.main,
    color: "#00344E",
  },
  colorContainer: {
    display: "flex",
    alignItems: "center",
  },
  colorBox: {
    width: 6,
    height: 16,
    marginRight: "4px",
    borderRadius: "2px",
  },
  fieldContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
  },
  inputLabel: {
    flex: 1,
    paddingRight: "12px",
  },
  defaultFlex: {
    flex: 1,
  },
}));

export default CreateVirtualRouteDialog;
